import { Box, Typography, Button, IconButton, Stack, alpha, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { StyledAccordion, StyledAccordionSummary, StyledAccordionDetails } from './styled';
import ProfileInfo from './ProfileInfo';
import { LinkedProfile, Document } from './types';
import { checkProfileCompleteness } from "../../utils/profileUtils";
import { useTranslation } from 'react-i18next';

interface ProfileAccordionProps {
  profile: LinkedProfile;
  index: number;
  onEdit: (profileId: number) => void;
  onUnlink: (profile: LinkedProfile) => void;
  onMakeMainRenter: (profileId: number) => void;
  onDocumentClick?: (document: Document) => void;
}

const ProfileAccordion = ({ 
  profile, 
  index, 
  onEdit, 
  onUnlink, 
  onMakeMainRenter,
  onDocumentClick
}: ProfileAccordionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  if (!profile.profile) return null;
  
  const isMainRenter = profile.profile.screeningType === 'RENTER' && index === 0;
  const isRenter = profile.profile.screeningType === 'RENTER';
  const validationResult = checkProfileCompleteness(profile.profile, profile.documents || []);
  const isIncomplete = !validationResult.isComplete;
  
  return (
    <Box sx={{
      margin: { xs: "0 -0.5rem", sm: "0 -1rem" }
     }} key={profile.id}>
      <StyledAccordion
        sx={{
          border: 0,
          padding: { xs: ".25rem .5rem", sm: ".5rem 1rem" },
          borderRadius: "0px !important",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <StyledAccordionSummary
          sx={{
            backgroundColor: "#fff",
            borderRadius: 0,
            color: "#000",
            padding: { xs: '0.75rem 0', sm: 0 },
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1.5, sm: 2 },
            minHeight: { xs: 'auto', sm: '48px' },
            position: 'relative',
            '& .MuiAccordionSummary-expandIconWrapper': {
              position: { xs: 'absolute', sm: 'static' },
              top: { xs: '0.75rem', sm: 'auto' },
              right: { xs: '0.75rem', sm: 'auto' },
              zIndex: 1,
              padding: { xs: '0.5rem', sm: 0 }
            }
          }}
          expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
        >
          <Stack 
            direction="row" 
            spacing={2} 
            alignItems="center" 
            sx={{ 
              flex: 1,
              padding: 0,
              width: '100%',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 2 },
              pr: { xs: 2.5, sm: 0 }
            }}
          >
            <Stack 
              direction="row" 
              spacing={1} 
              alignItems="center" 
              sx={{ 
                flex: 1,
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                gap: 1
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: { xs: '1rem', sm: 'inherit' } }}>
                {profile.profile.firstname} {profile.profile.surname}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: 'inherit' } }}>
                {profile.profile.screeningType === "RENTER" ? 
                  t('screening_profile.renter_number', { number: index + 1 }) : 
                  t('screening_profile.guarantor_number', { number: index + 1 })}
              </Typography>
              {isMainRenter && (
                <Chip 
                  label={t('screening_profile.main_renter')}
                  size="small" 
                  sx={{ 
                    backgroundColor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                    fontWeight: 500,
                    height: { xs: '24px', sm: 'auto' }
                  }}
                />
              )}
              {isIncomplete && (
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label={t('screening_profile.incomplete')}
                  size="small"
                  sx={{
                    backgroundColor: 'red',
                    color: '#FFF',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    height: { xs: '24px', sm: 'auto' },
                    '& .MuiChip-icon': {
                      color: '#fff',
                      fontSize: { xs: '0.875rem', sm: 'inherit' }
                    }
                  }}
                />
              )}
            </Stack>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
              gap: 1,
              marginTop: { xs: 1, sm: 0 }
            }}>
              {(isRenter && !isMainRenter) && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMakeMainRenter(profile.id);
                  }}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    borderRadius: '4px',
                    backgroundColor: alpha('#d32f2f', 0.1),
                    color: 'secondary.main',
                    border: 0,
                    padding: { xs: '0.5rem 1rem', sm: '0.375rem 1rem' },
                    minHeight: { xs: '36px', sm: 'auto' },
                    '&:hover': {
                      color: '#fff',
                      border: 0,
                      backgroundColor: 'secondary.main',
                    }
                  }}
                >
                  {t('screening_profile.make_main_renter')}
                </Button>
              )}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(profile.profileId);
                }}
                size="small"
                sx={{ 
                  borderRadius: '4px',
                  backgroundColor: alpha('#d32f2f', 0.1),
                  color: 'secondary.main',
                  padding: { xs: '0.75rem', sm: '0.5rem' },
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnlink(profile);
                  }}
                  size="small"
                  sx={{ 
                    borderRadius: '4px',
                    backgroundColor: alpha('#d32f2f', 0.1),
                    color: 'secondary.main',
                    padding: { xs: '0.75rem', sm: '0.5rem' },
                    '&:hover': {
                      color: '#fff',
                      backgroundColor: 'secondary.main',
                    }
                  }}
                >
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Stack>
        </StyledAccordionSummary>
        <StyledAccordionDetails sx={{ padding: { xs: '16px 1rem', sm: '16px 2rem' } }}>
          <ProfileInfo 
            profile={profile.profile} 
            documents={profile.documents || []} 
            onDocumentClick={onDocumentClick}
          />
        </StyledAccordionDetails>
      </StyledAccordion>
    </Box>
  );
};

export default ProfileAccordion; 